/**
 * "When students see it" for course content (scheduled posting).
 *
 * Form fields:
 *   publishMode  'now' | 'afterClass' | 'later'
 *   publishDate  'YYYY-MM-DD' (Chicago)   — for 'later'
 *   publishTime  'HH:mm'      (Chicago)   — for 'later'
 *
 * The server stores publish_at in UTC; students don't see the item before then,
 * and the email (if on) is sent when it goes live.
 */
import { DateTime } from 'luxon';

const ZONE = 'America/Chicago';
export const getChicagoNow = () => DateTime.now().setZone(ZONE);
const createChicagoDateTime = (date, time) => {
  if (!date || !time) return null;
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: ZONE });
  return dt.isValid ? dt : null;
};
/** DB value ('YYYY-MM-DD HH:mm:ss' UTC, or ISO) → Chicago DateTime. */
const utcToChicago = (value) => {
  if (!value) return null;
  const dt = value instanceof Date
    ? DateTime.fromJSDate(value)
    : DateTime.fromISO(String(value).replace(' ', 'T'), { zone: 'utc' });
  return dt.setZone(ZONE);
};

export const EMPTY_PUBLISH_FIELDS = { publishMode: 'now', publishDate: '', publishTime: '' };

/** Today's class end in Chicago (course.virtual_end_time 'HH:mm[:ss]'), if it's still ahead. */
export function afterClassToday(courseOrElective, now = getChicagoNow()) {
  const end = courseOrElective?.virtual_end_time;
  if (!end) return null;
  const dt = createChicagoDateTime(now.toISODate(), String(end).slice(0, 5));
  return dt && dt > now ? dt : null;
}

/** Form → ISO UTC string to send as publishAt, or null for "now".
 *  "After class" stores today's date + class end time in publishDate/publishTime
 *  when it's picked, so both scheduled modes read the same two fields. */
export function getPublishAtFromForm(formData) {
  const mode = formData?.publishMode || 'now';
  if ((mode === 'later' || mode === 'afterClass') && formData.publishDate) {
    const dt = createChicagoDateTime(formData.publishDate, formData.publishTime || '08:00');
    return dt ? dt.toUTC().toISO() : null;
  }
  return null;
}

/** Existing item → form fields (for editing). */
export function publishFieldsFromItem(item) {
  if (!item?.publish_at || !isScheduledItem(item)) return { ...EMPTY_PUBLISH_FIELDS };
  const dt = utcToChicago(item.publish_at);
  if (!dt?.isValid) return { ...EMPTY_PUBLISH_FIELDS };
  return { publishMode: 'later', publishDate: dt.toISODate(), publishTime: dt.toFormat('HH:mm') };
}

/** Still hidden from students? */
export function isScheduledItem(item, now = getChicagoNow()) {
  if (!item?.publish_at) return false;
  const dt = utcToChicago(item.publish_at);
  return !!dt?.isValid && dt > now;
}

/** "Tue, Oct 21 · 7:00 PM" (Chicago). */
export function formatPublishAt(value) {
  const dt = value && typeof value.toFormat === 'function' ? value : utcToChicago(value);
  return dt?.isValid ? `${dt.toFormat('ccc, LLL d')} · ${dt.toFormat('h:mm a')}` : '';
}

/** Problem with the chosen time, or null. Picking "now" is always fine. */
export function publishTimingError(formData, now = getChicagoNow()) {
  const mode = formData?.publishMode || 'now';
  if (mode === 'now') return null;
  if (!formData.publishDate) return 'Pick the date students should see it.';
  const dt = createChicagoDateTime(formData.publishDate, formData.publishTime || '08:00');
  if (!dt || !dt.isValid) return 'That date or time doesn\u2019t look right.';
  if (dt <= now) return 'That time has already passed \u2014 pick a later time, or choose Now.';
  return null;
}

/** Shows the problem (if any) and returns true when the form shouldn't submit. */
export function blockOnBadPublishTime(formData, notify) {
  const err = publishTimingError(formData);
  if (err) notify('error', 'Check the posting time', err);
  return !!err;
}
